import type { PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type Attributes = Record<string, unknown>;

export type AnalyticsPayload = {
	type: string;
	action?: string;
	actionSubject?: string;
	actionSubjectId?: string;
	name?: string;
	attributes?: Attributes;
};

export type AnalyticsContextShape = {
	fireAnalytics: (analyticsPayload: AnalyticsPayload) => void;
};

export const AnalyticsContext = createContext<AnalyticsContextShape>({
	fireAnalytics: () => {},
});

export type AnalyticsProviderProps = PropsWithChildren<{
	attributes?: Attributes;
}>;

export const AnalyticsProvider = ({ attributes = {}, children }: AnalyticsProviderProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalytics = useCallback(
		({
			type,
			action,
			actionSubject,
			actionSubjectId,
			name,
			attributes: additionalAttributes = {},
		}: AnalyticsPayload) => {
			createAnalyticsEvent({
				type,
				data: {
					source: 'trialEndLockScreen',
					action,
					actionSubject,
					actionSubjectId,
					name,
					attributes: {
						productKey: 'confluence.ondemand',
						...attributes,
						...additionalAttributes,
					},
				},
			}).fire();
		},
		[attributes, createAnalyticsEvent],
	);

	const analyticsContextValue = useMemo(() => ({ fireAnalytics }), [fireAnalytics]);

	return (
		<AnalyticsContext.Provider value={analyticsContextValue}>{children}</AnalyticsContext.Provider>
	);
};

export const useAnalytics = () => useContext(AnalyticsContext);
